import { isEmpty, isNil, replace } from 'lodash/fp'
import axios, { AxiosHeaders, RawAxiosRequestHeaders, type Method } from 'axios'
import { inspect } from '../inspect'
import { AppEntrySubscription } from '@mzc-watchover/terashite-entry-graphql'

import log from 'loglevel'
import { RegistrationStatus, FormInputDatas } from '../types'
import { EnumPaymentGmoPgStatus } from '@mzc-watchover/payment-graphql'
import { assign } from 'lodash'
import { getCustomHeaders } from '../components/Admin/functions'
import { Entry } from '../../../cdk/terashiteEntry/lib/container-api/src/types'

const env = process.env['REACT_APP_ENV'] ?? 'dev'

const baseName = 'terashite.jp'
const zoneName = `${{ prd: '', stg: '', int: 'dev.', dev: 'dev.', exp: 'dev.' }[env] as string
  }${baseName}`
export const domainName = `api${{ prd: '', stg: '-stg', int: '-int', dev: '', exp: '-exp' }[env] as string
  }.terashite-entry.${zoneName}`

export const trimSpace = (value: string): string => replace(/[　 ]/g, '')(value)

export const authUser = async (
  sub: string
): Promise<
  | {
    success: boolean
    memberId?: string
  }
  | undefined
> => {
  const result = await httpRequest(
    'get',
    `https://${domainName}/v1/users/auth`,
    undefined,
    {
      Authorization: sub
    }
  )
  if (isNil(result)) {
    log.error('[common.getSubscriptionData]', inspect({ result }))
    return undefined
  }
  return result.data
}

export const getSubscriptionData = async (
  memberId: string
): Promise<AppEntrySubscription | undefined> => {
  const result = await httpRequest(
    'get',
    `https://${domainName}/v1/users/subscription/get/${memberId}`
  )
  if (isNil(result)) {
    log.error('[common.getSubscriptionData]', inspect({ result }))
    return undefined
  }
  log.debug(inspect({ result }))
  const { status, data } = result
  if (status !== 200) {
    log.error(`[functions.getEntryData]`, inspect(result))
    return undefined
  }
  return data
}
export const updateSubscription = async (
  data: AppEntrySubscription,
  sub: string
): Promise<boolean | undefined> => {
  log.debug('[common.updateSubscription]', inspect({ data }))
  const result = await httpRequest(
    'put',
    `https://${domainName}/v1/users/${data.memberId}`,
    {
      data
    },
    { authorization: sub }
  )
  log.debug('[common.updateSubscription]', inspect({ result }))
  if (isNil(result)) {
    log.error(
      '[common.getSubscriptionData] faild to update subscription',
      inspect({ result })
    )
    return undefined
  }
  if (result.status === 202) {
    return true
  }
  log.error(`[functions.getEntryData]`, inspect(result))
  return false
}
export const updateMail = async (
  memberId: string,
  mail: string,
  sub: string
): Promise<boolean | undefined> => {
  log.debug('[common.updateMail]', inspect({ memberId, mail }))
  if (isEmpty(memberId) || isEmpty(mail)) {
    log.error('[common.updateMail] Either member id or email is empty')
    return undefined
  }
  const result = await httpRequest(
    'patch',
    `https://${domainName}/v1/users/${memberId}`,
    {
      data: { mail }
    },
    { authorization: sub }
  )
  if (isNil(result)) {
    log.error(
      '[common.updateMail] faild to update subscription'
    )
    return undefined
  }
  if (result.status === 200) {
    return true
  }
  log.error(`[common.updateMail]`, inspect(result))
  return false
}
export const updateEntry = async (data: FormInputDatas) => {
  const result = await axios.post(
    `https://${domainName}/v1/users/entry/update`,
    {
      data
    }
  )
  log.debug('[common.updateSubscription]', inspect({ result }))
  if (result.status === 202) {
    return result.data
  }
  log.error(`[functions.getEntryData]`, inspect(result))
  return undefined
}
export const getCustomerList = async () => {
  const customHeaders = getCustomHeaders() as AxiosHeaders | undefined
  if (isNil(customHeaders)) {
    return undefined
  }
  const result = await httpRequest(
    'get',
    `https://${domainName}/v1/admin/users`,
    undefined,
    {
      Authorization: customHeaders.get('Authorization')
    }
  )
  log.debug(inspect({ result }))
  if (isNil(result)) {
    log.error('[common.getInvoices]failed to get contract lsit')
    return undefined
  }
  if (result.status === 200) {
    return result.data
  }
  log.error(`[functions.getCustomerList]`, inspect(result))
  return undefined
}
export const getCustomerInfo = async (
  memberId: string
): Promise<
  | {
    status: RegistrationStatus
    subscription: AppEntrySubscription | undefined
    paymentSubscriptions: string[] | undefined
    entry: Entry | undefined
  }
  | undefined
> => {
  const customHeaders = getCustomHeaders() as AxiosHeaders | undefined
  if (isNil(customHeaders)) {
    return undefined
  }
  const response = await httpRequest(
    'get',
    `https://${domainName}/v1/admin/users/${memberId}`,
    undefined,
    {
      Authorization: customHeaders.get('Authorization')
    }
  )
  log.debug(inspect({ response }))
  if (isNil(response)) {
    log.error('[common.getCustomerInfo]failed to get contract info')
    return undefined
  }
  if (response.status === 200) {
    return response.data
  }
  log.error(`[functions.getCustomerInfo]`, inspect(response))
  return undefined
}
export const updateMember = async (
  kind: RegistrationStatus,
  entry: FormInputDatas | undefined,
  subscription: AppEntrySubscription | undefined
): Promise<boolean | undefined> => {
  if (isNil(entry?.memberId) && isNil(subscription?.memberId)) {
    log.error(
      '[common.updateMember] memberId is nil',
      inspect({ kind, entry, subscription })
    )
    return undefined
  }
  const memberId =
    kind === RegistrationStatus.entry ? entry?.memberId : subscription?.memberId
  const customHeaders = getCustomHeaders() as AxiosHeaders | undefined
  if (isNil(customHeaders)) {
    return undefined
  }
  log.debug('[common.updateMember]', inspect({ entry }))
  const response = await httpRequest(
    'put',
    `https://${domainName}/v1/admin/users/${memberId}`,
    {
      kind,
      entry,
      subscription
    },
    {
      Authorization: customHeaders.get('Authorization')
    }
  )
  log.debug(response)
  if (isNil(response)) {
    log.error(
      '[common.updateMember] cannot update member info',
      inspect({ kind, entry, subscription })
    )
    return undefined
  }
  const { status, data: responseData } = response
  if (status !== 200 || responseData === false) {
    log.error(
      '[common.updateMember] faild to update member info',
      inspect({ kind, entry, subscription })
    )
    return undefined
  }
  return responseData
}
export const requestGetInvoices = async (
  paymentAccountNumber: string,
  sub: string
): Promise<
  | Record<
    string,
    Array<{
      effectiveDate: string
      amount: number
      status: EnumPaymentGmoPgStatus
    }>
  >
  | undefined
> => {
  const response = await httpRequest(
    'get',
    `https://${domainName}/v1/users/payment/invoices/${paymentAccountNumber}`,
    undefined,
    { Authorization: sub }
  )
  if (isNil(response)) {
    log.error(
      '[common.getInvoices] fail get invoices request',
      inspect({
        paymentAccountNumber,
        sub
      })
    )
    return undefined
  }
  const { status, data } = response
  if (status !== 200) {
    log.error(
      '[common.getInvoices] cannot get invoices',
      inspect({
        status,
        data,
        paymentAccountNumber,
        sub
      })
    )
    return undefined
  }
  return data
}
export const httpRequest = async (
  method: Method,
  url: string,
  body?: any,
  headers?: RawAxiosRequestHeaders | AxiosHeaders,
  params?: any
): Promise<{ status: number; data: any } | undefined> => {
  try {
    axios.interceptors.response.use(
      (response) => response,
      (error) => error.response
    )
    const config = {
      url,
      method
    }
    if (!isNil(body)) {
      assign(config, {
        data: body
      })
    }
    if (!isNil(headers)) {
      assign(config, {
        headers
      })
    }
    if (!isNil(params)) {
      assign(config, {
        params
      })
    }
    const response = await axios(config)
    const { status, data } = response
    return { status, data }
  } catch (error) {
    log.error(`[common.httpRequest] request failed`, inspect({ error }))
    return undefined
  }
}
